/**
 * Controle de export do Controle PLD (#205) — CSV/XLSX (multi-aba) + PDF do
 * submercado + mês selecionados. Lê os mesmos hooks da Visão Geral (cache
 * compartilhado, sem fetch extra). Inline no header do Controle PLD.
 */
import { useMemo, useState } from "react";
import { FileText } from "lucide-react";
import type { PldSubmercado } from "../../../api/types";
import { usePldOverview } from "../../../hooks/usePldOverview";
import { usePldProjecao } from "../../../hooks/usePldProjecao";
import { usePldSemanas } from "../../../hooks/usePldSemanas";
import { gerarPdfRelatorio } from "../../../utils/pdfExport";
import { ExportDropdown } from "../../ui/ExportDropdown";
import { buildPldExport } from "./pldExport";

interface Props {
  mesRef: string;
  submercado: PldSubmercado;
}

export function PldExportControl({ mesRef, submercado }: Props) {
  const overview = usePldOverview(mesRef, submercado);
  const projecao = usePldProjecao(mesRef, submercado);
  const semanas = usePldSemanas(mesRef, submercado);
  const [pdfBusy, setPdfBusy] = useState(false);

  const built = useMemo(
    () =>
      buildPldExport({
        mesRef,
        submercado,
        overview: overview.data,
        projecao: projecao.data,
        semanas: semanas.data,
      }),
    [mesRef, submercado, overview.data, projecao.data, semanas.data],
  );

  if (!built) return null; // ainda sem dado pra exportar

  function handlePdf() {
    if (!built) return;
    setPdfBusy(true);
    gerarPdfRelatorio(built.pdfInput).finally(() => setPdfBusy(false));
  }

  return (
    <div className="flex items-center gap-2" data-testid="pld-export-control">
      <ExportDropdown
        filename={built.filename}
        primarySheetName="PLD diário"
        columns={built.mainColumns}
        rows={built.mainRows}
        extraSheets={built.extraSheets}
        extraSheetsLabel="Tudo (Excel)"
      />
      <button
        type="button"
        onClick={handlePdf}
        disabled={pdfBusy}
        data-testid="pld-export-pdf"
        className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 border border-white/10 rounded-md hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50"
      >
        <FileText size={14} />
        {pdfBusy ? "Gerando…" : "Relatório PDF"}
      </button>
    </div>
  );
}
